import { useNavigate } from 'react-router-dom';

const endpoints = [
  {
    method: 'GET',
    path: '/api/v1/consultancies',
    description: 'List all consultancies with optional filters (city, country, rating)',
    auth: true,
  },
  {
    method: 'GET',
    path: '/api/v1/consultancies/:id',
    description: 'Get detailed profile of a specific consultancy',
    auth: false,
  },
  {
    method: 'POST',
    path: '/api/v1/appointments',
    description: 'Book an appointment with a consultancy',
    auth: true,
  },
  {
    method: 'GET',
    path: '/api/v1/appointments',
    description: 'List all appointments for the authenticated user',
    auth: true,
  },
  {
    method: 'GET',
    path: '/api/v1/students',
    description: 'List all students in your consultancy CRM',
    auth: true,
  },
  {
    method: 'POST',
    path: '/api/v1/students',
    description: 'Add a new student to your CRM',
    auth: true,
  },
  {
    method: 'GET',
    path: '/api/v1/leads',
    description: 'Get all leads in your pipeline',
    auth: true,
  },
  {
    method: 'POST',
    path: '/api/v1/leads',
    description: 'Create a new lead in your pipeline',
    auth: true,
  },
  {
    method: 'POST',
    path: '/api/v1/ai/advisor',
    description: 'Get AI-powered study abroad guidance',
    auth: true,
  },
];

export default function Docs() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-gray-100">
        <div className="w-full px-4 md:px-8 lg:px-12 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate('/')} className="text-xl font-bold text-navy-600 cursor-pointer">
              EzyAbroad
            </button>
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-500 hover:text-navy-600 cursor-pointer"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </header>

      <main className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="mb-10">
            <span className="text-xs font-semibold tracking-widest text-gold-600 uppercase">API Documentation</span>
            <h1 className="mt-3 text-3xl md:text-4xl font-bold text-navy-600">
              EzyAbroad API Reference
            </h1>
            <p className="mt-3 text-base text-gray-500">
              Build integrations with the EzyAbroad platform using our REST API.
            </p>
          </div>

          {/* Getting Started */}
          <div className="bg-gray-50 rounded-xl p-6 md:p-8 mb-10">
            <h2 className="text-lg font-semibold text-navy-600 mb-4">Getting Started</h2>
            <div className="space-y-4 text-sm text-gray-600">
              <p>
                All API requests should be made to <code className="bg-white px-2 py-1 rounded text-navy-600 font-mono text-xs">https://api.ezyabroad.com/v1</code>
              </p>
              <p>
                Authentication is required for most endpoints. Include your API key in the Authorization header:
              </p>
              <div className="bg-navy-700 rounded-lg p-4 overflow-x-auto">
                <code className="text-green-400 font-mono text-xs">
                  Authorization: Bearer sk_your_api_key_here
                </code>
              </div>
              <p>
                To get your API key, log in to your consultancy dashboard and navigate to Settings → API Keys.
              </p>
            </div>
          </div>

          {/* Endpoints */}
          <h2 className="text-lg font-semibold text-navy-600 mb-4">Endpoints</h2>
          <div className="space-y-3">
            {endpoints.map((endpoint, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 hover:border-gold-300 transition-colors"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className={`text-xs font-bold px-2.5 py-1 rounded ${
                      endpoint.method === 'GET'
                        ? 'bg-sky-100 text-sky-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {endpoint.method}
                  </span>
                  <code className="text-sm font-mono text-navy-600">{endpoint.path}</code>
                  {endpoint.auth && (
                    <span className="text-xs bg-gold-100 text-gold-700 px-2 py-0.5 rounded">Auth Required</span>
                  )}
                </div>
                <p className="mt-2 text-sm text-gray-500">{endpoint.description}</p>
              </div>
            ))}
          </div>

          {/* Rate Limits */}
          <div className="mt-10 bg-gray-50 rounded-xl p-6 md:p-8">
            <h2 className="text-lg font-semibold text-navy-600 mb-4">Rate Limits</h2>
            <div className="text-sm text-gray-600 space-y-2">
              <p>Basic Plan: 100 requests/minute</p>
              <p>Pro Plan: 500 requests/minute</p>
              <p>Premium Plan: 2,000 requests/minute</p>
              <p className="text-gray-400 mt-3">
                Rate limit headers are included in all API responses: <code className="bg-white px-1.5 py-0.5 rounded text-xs font-mono">X-RateLimit-Remaining</code> and <code className="bg-white px-1.5 py-0.5 rounded text-xs font-mono">X-RateLimit-Reset</code>.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
